import { useState, useEffect, useRef } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
//Components
import { translateMessage } from "@delibrandev/utils";
import { /*getAlergens,*/ updateAlergens } from "../../../../../api/products";
import {
  setAlert,
  setLoading,
  updateFloatPanelData,
} from "../../../../../reducers/userConfigSlice";
//Imagenes alergenos
import celery from "./../../../../../assets/allergens/celery.png";
import crustaceans from "./../../../../../assets/allergens/crustaceans.png";
import eggs from "./../../../../../assets/allergens/eggs.png";
import fish from "./../../../../../assets/allergens/fish.png";
import gluten from "./../../../../../assets/allergens/gluten.png";
import lactose from "./../../../../../assets/allergens/lactose.png";
import lupin from "./../../../../../assets/allergens/lupin.png";
import molluscs from "./../../../../../assets/allergens/molluscs.png";
import mustard from "./../../../../../assets/allergens/mustard.png";
import nuts from "./../../../../../assets/allergens/nuts.png";
import peanuts from "./../../../../../assets/allergens/peanuts.png";
import sesame from "./../../../../../assets/allergens/sesame.png";
import soy from "./../../../../../assets/allergens/soy.png";
import sulfites from "./../../../../../assets/allergens/sulfites.png";

const allergenNames = {
  celery: "Apio",
  crustaceans: "Crustáceos",
  eggs: "Huevos",
  fish: "Pescado",
  gluten: "Gluten",
  lactose: "Lactosa",
  lupin: "Altramuces",
  molluscs: "Moluscos",
  mustard: "Mostaza",
  nuts: "Frutos secos",
  peanuts: "Cacahuetes",
  sesame: "Sésamo",
  soy: "Soja",
  sulfites: "Sulfitos",
};

const allergenImages = {
  celery,
  crustaceans,
  eggs,
  fish,
  gluten,
  lactose,
  lupin,
  molluscs,
  mustard,
  nuts,
  peanuts,
  sesame,
  soy,
  sulfites,
};

export const normalizeAllergens = (value) => {
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value && typeof value === "object"
    ? { ...(value.allergens || value) }
    : {};
};

export const emptyAllergenSelection = Object.fromEntries(
  Object.keys(allergenNames).map((key) => [key, false]),
);

export const Alergens = ({
  draftValue,
  onDraftChange,
  disabled = false,
  reference = false,
}) => {
  const dispatch = useDispatch();
  const allAlergens = useSelector(
    (state) => state.aplicationConfig.corporationData.alergens,
  );
  const savedSelection = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.data?.allergens,
  );
  const selected = onDraftChange ? draftValue : savedSelection;
  const idProduct = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.queryId,
  );
  const [alergens, setAlergens] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const prepareAlergens = () => {
    // 1) Normalizamos: aseguramos que existan todas las keys del catálogo
    const normalized = normalizeAllergens(selected);
    for (const key of allAlergens) {
      if (!(key in normalized)) normalized[key] = false;
    }

    // 2) Construimos el array priorizando el orden de allAlergens
    const ordered = reference
      ? [
          "gluten",
          "lactose",
          "eggs",
          "nuts",
          "peanuts",
          "soy",
          "sesame",
          "lupin",
          "mustard",
          "fish",
          "crustaceans",
          "molluscs",
          "celery",
          "sulfites",
        ].filter((key) => allAlergens.includes(key))
      : allAlergens;
    const listed = [
      ...ordered
        .filter((key) => allergenNames[key])
        .map((key) => ({
          id: key,
          name:
            reference && key === "lactose"
              ? "Leche"
              : reference && key === "eggs"
                ? "Huevo"
                : allergenNames[key],
          icon: allergenImages[key],
          selected: !!normalized[key],
        })),
      // 3) Añadimos cualquier clave extra que pueda venir en `selected`
      ...Object.keys(normalized)
        .filter((key) => !allAlergens.includes(key) && allergenNames[key])
        .map((key) => ({
          id: key,
          name:
            reference && key === "lactose"
              ? "Leche"
              : reference && key === "eggs"
                ? "Huevo"
                : allergenNames[key],
          icon: allergenImages[key],
          selected: !!normalized[key],
        })),
    ];

    setAlergens(listed);
  };

  useEffect(() => {
    prepareAlergens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    prepareAlergens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, allAlergens, reference]);

  const update = async (alergen) => {
    if (disabled || savingRef.current) return;
    if (onDraftChange) {
      onDraftChange({
        ...normalizeAllergens(selected),
        [alergen.id]: !alergen.selected,
      });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const updatedSelected = {
      ...normalizeAllergens(selected),
      [alergen.id]: !alergen.selected,
    };
    dispatch(setLoading(true));
    try {
      const response = await updateAlergens(idProduct, {
        allergens: updatedSelected,
      });
      if (response && !response.error) {
        dispatch(
          updateFloatPanelData({ name: "allergens", data: updatedSelected }),
        );
      } else {
        dispatch(
          setAlert(translateMessage(response?.message || "Bad request")),
        );
      }
    } catch {
      dispatch(setAlert(translateMessage("Bad request")));
    } finally {
      savingRef.current = false;
      setSaving(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      {reference ? (
        <label className="referenceNoAllergens">
          <span>
            <strong>Sin alérgenos</strong>
            <small>Márcalo si el producto no declara ninguno</small>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label="Sin alérgenos"
            disabled={disabled}
            checked={alergens.length > 0 && alergens.every((a) => !a.selected)}
            onChange={(e) => {
              if (e.target.checked && onDraftChange)
                onDraftChange({ ...emptyAllergenSelection });
            }}
          />
        </label>
      ) : (
        <div className="inputDescription">Active o desactive alérgenos</div>
      )}
      <div
        className="alergen-organitation-area"
        style={{ width: "100%" }}
        aria-busy={saving}
        data-editor-write-pending={saving}
      >
        {alergens.length > 0 &&
          alergens.map((alergen) => (
            <button
              type="button"
              disabled={saving || disabled}
              aria-pressed={alergen.selected}
              className={
                alergen.selected
                  ? "alergen-organitation-area-option"
                  : "alergen-organitation-area-option-noselected"
              }
              key={alergen.id}
              onClick={() => update(alergen)}
            >
              <img src={`${alergen.icon}`} alt="" width="15px" />{" "}
              <div>{alergen.name}</div>
            </button>
          ))}
      </div>
      {reference && (
        <p className="referenceAllergenCount">
          {alergens.filter((a) => a.selected).length} de {alergens.length}{" "}
          alérgenos seleccionados
        </p>
      )}
    </>
  );
};
